/**
 * Deal Box Share Dialog Component
 * Share file with specific roles or users
 */

import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDealBox } from '@/hooks/useDealBox';
import { DEAL_BOX_TRANSLATIONS, ROLE_LABELS } from '@/constants/deal-box.constants';
import type { DealFile, VisibilityRole } from '@/types/deal-box.types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Share2, Users, User, AlertCircle } from 'lucide-react';

interface DealBoxShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  file: DealFile;
  dealId: string;
  onSuccess: () => void;
}

export function DealBoxShareDialog({
  open,
  onOpenChange,
  file,
  dealId,
  onSuccess,
}: DealBoxShareDialogProps) {
  const { language } = useLanguage();
  const t = DEAL_BOX_TRANSLATIONS[language as 'en' | 'fr'] || DEAL_BOX_TRANSLATIONS.en;
  const roleLabels = ROLE_LABELS[language as 'en' | 'fr'] || ROLE_LABELS.en;

  const { shareFile, loading, error } = useDealBox(dealId);

  const [shareType, setShareType] = useState<'role' | 'user'>('role');
  const [selectedRole, setSelectedRole] = useState<VisibilityRole | null>(null);
  const [userId, setUserId] = useState('');

  // Available roles to share with (exclude roles already visible)
  const allRoles: VisibilityRole[] = ['seller', 'buyer', 'broker', 'lender', 'consultant', 'partner', 'staff', 'admin'];
  const availableRoles = allRoles.filter(role => !file.visibilityRoles.includes(role));

  // Handle share
  const handleShare = async () => {
    if (shareType === 'role' && !selectedRole) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner un rôle' : 'Please select a role');
      return;
    }

    if (shareType === 'user' && !userId.trim()) {
      toast.error(language === 'fr' ? 'Veuillez entrer un ID utilisateur' : 'Please enter a user ID');
      return;
    }

    const success = await shareFile({
      fileId: file.id,
      dealId,
      shareToRole: shareType === 'role' ? selectedRole as VisibilityRole : undefined,
      shareToUserId: shareType === 'user' ? userId.trim() : undefined,
    });

    if (success) {
      onSuccess();
      // Reset form
      setSelectedRole(null);
      setUserId('');
    } else {
      toast.error(t.shareError);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            {t.shareFile}
          </DialogTitle>
          <DialogDescription>
            {language === 'fr' 
              ? `Partager "${file.title}" avec d'autres utilisateurs`
              : `Share "${file.title}" with other users`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Share Type Selection */}
          <RadioGroup 
            value={shareType} 
            onValueChange={(v) => setShareType(v as 'role' | 'user')}
            className="grid grid-cols-2 gap-4"
          >
            <div>
              <RadioGroupItem
                value="role"
                id="share-role"
                className="peer sr-only"
              />
              <Label
                htmlFor="share-role"
                className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
              >
                <Users className="h-6 w-6 mb-2" />
                <span className="text-sm font-medium">
                  {language === 'fr' ? 'Par rôle' : 'By Role'}
                </span>
              </Label>
            </div>

            <div>
              <RadioGroupItem
                value="user"
                id="share-user"
                className="peer sr-only"
              />
              <Label
                htmlFor="share-user"
                className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
              >
                <User className="h-6 w-6 mb-2" />
                <span className="text-sm font-medium">
                  {language === 'fr' ? 'Utilisateur spécifique' : 'Specific User'}
                </span>
              </Label>
            </div>
          </RadioGroup>

          {/* Role Selection */}
          {shareType === 'role' && (
            <div className="space-y-2">
              <Label>
                {language === 'fr' ? 'Sélectionner un rôle' : 'Select a role'}
              </Label>
              {availableRoles.length > 0 ? (
                <Select value={selectedRole || ''} onValueChange={(v) => setSelectedRole(v as VisibilityRole)}>
                  <SelectTrigger>
                    <SelectValue placeholder={language === 'fr' ? 'Choisir un rôle...' : 'Choose a role...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map(role => (
                      <SelectItem key={role} value={role}>
                        {roleLabels[role]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' 
                    ? 'Ce fichier est déjà visible par tous les rôles'
                    : 'This file is already visible to all roles'
                  }
                </p>
              )}
            </div>
          )}

          {/* User ID Input */}
          {shareType === 'user' && (
            <div className="space-y-2">
              <Label htmlFor="user-id">
                {language === 'fr' ? 'ID Utilisateur' : 'User ID'}
              </Label>
              <Input
                id="user-id"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder={language === 'fr' ? 'Entrer l\'ID utilisateur...' : 'Enter user ID...'}
              />
              <p className="text-xs text-muted-foreground">
                {language === 'fr' 
                  ? 'L\'utilisateur recevra une notification'
                  : 'The user will receive a notification'
                }
              </p>
            </div>
          )}

          {/* Current Visibility */}
          <div className="space-y-2 pt-2 border-t">
            <Label className="text-muted-foreground text-xs uppercase">
              {language === 'fr' ? 'Visibilité actuelle' : 'Current visibility'}
            </Label>
            <div className="flex flex-wrap gap-1">
              {file.visibilityRoles.map(role => (
                <span 
                  key={role} 
                  className="px-2 py-0.5 bg-secondary text-secondary-foreground rounded text-xs"
                >
                  {roleLabels[role]}
                </span>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button 
            onClick={handleShare} 
            disabled={loading || (shareType === 'role' && !selectedRole) || (shareType === 'user' && !userId.trim())}
          >
            <Share2 className="h-4 w-4 mr-2" />
            {loading 
              ? (language === 'fr' ? 'Partage...' : 'Sharing...')
              : t.shareFile
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
